import React, { useEffect, useRef, useState } from "react";
import {
  Typography,
  Card,
  CardContent,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mhome/ui";
import LinearProgress from "@mui/material/LinearProgress";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import EventAvailableIcon from "@mui/icons-material/EventAvailable";
import EventBusyIcon from "@mui/icons-material/EventBusy";
import TokenIcon from "@mui/icons-material/Token";
import AutoModeIcon from "@mui/icons-material/AutoMode";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useHistory } from "react-router-dom";
import {
  getUsage,
  getPayments,
  startCheckout,
  cancelSubscription,
} from "../service/billing-service";
import useApiCall from "../common/api-call";
import { useWebSocket } from "../ws/websocket";
import { useCustomFormat } from "../views/after-init";
import dayjs from "dayjs";
import { useRecoilState, useRecoilValue } from "recoil";
import { platformState, wsReadyState } from "../global-state";
import {
  useIsSmallScreen,
  useComponentBackgroundColor,
} from "../common/adaptive-theme-provider";
import { useToast } from "../common/toast";

const Usage = () => {
  const { apiCall } = useApiCall();
  const { sendAndWaitWs } = useWebSocket();
  const history = useHistory();
  const isSmallScreen = useIsSmallScreen();
  const customFormat = useCustomFormat();
  const { errorToast, successToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [credits, setCredits] = useState({});
  const [payments, setPayments] = useState([]);
  const [currentTime, setCurrentTime] = useState(dayjs());
  const [openTooltip, setOpenTooltip] = useState(null);
  const [platform] = useRecoilState(platformState);
  const wsReady = useRecoilValue(wsReadyState);
  const isApp = platform.source === "app";

  // Get the background color function with current global state
  const getComponentBgColor = useComponentBackgroundColor();

  useEffect(() => {
    if (wsReady) {
      handleGetUsage();
      handleGetPayments();
    }
  }, [wsReady]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(dayjs());
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  const handleGetUsage = async () => {
    setLoading(true);
    const resp1 = await getUsage(sendAndWaitWs);
    console.debug(resp1);
    setCredits(resp1 || {});
    setLoading(false);
  };

  const handleGetPayments = async () => {
    const resp = await getPayments(sendAndWaitWs);
    console.debug(resp);
    setPayments(resp?.payments || []);
  };

  const getPlanName = (plan) => {
    if (plan === "E") {
      return "Internal";
    }
    if (plan === "T") {
      return "Free";
    }
    if (plan === "V0") {
      return "Free";
    }
    if (plan === "V1") {
      return "Pro";
    }
    if (plan === "V1") {
      return "Max";
    }
    return "Unknown";
  };

  const getRefreshLabel = () => {
    return credits?.currentTier === credits?.refreshTier
      ? "Refresh on"
      : "Expire on";
  };

  const getUsagePercentage = () => {
    const total = credits?.totalBalance || 0;
    const used = credits?.usedBalance || 0;
    return total > 0 ? (used / total) * 100 : 0;
  };

  const formatBalance = (value) => {
    const formatted = (value || 0) / 100;
    return formatted % 1 === 0 ? formatted.toString() : formatted.toFixed(2);
  };

  const handleUpgrade = async () => {
    try {
      const resp = await startCheckout(apiCall, "pro");
      if (resp && resp.url) {
        window.location.href = resp.url;
      } else {
        errorToast("Failed to start checkout");
      }
    } catch (error) {
      errorToast("Error starting checkout");
    }
  };

  const handleCancelSubscription = async () => {
    setCancelDialogOpen(false);
    try {
      const resp = await cancelSubscription(apiCall);
      if (resp && !resp.error) {
        await handleGetUsage(); // Refresh usage data
        successToast("Subscription cancelled successfully");
      } else {
        errorToast("Failed to cancel subscription");
      }
    } catch (error) {
      errorToast("Error cancelling subscription");
    }
  };

  const isFreePlan = () => {
    const tier = credits?.currentTier;
    return tier === "T" || tier === "V0";
  };

  const isProPlan = () => {
    return credits?.currentTier === "V1";
  };

  const shouldShowUpgradeButton = () => {
    return !isApp && isFreePlan();
  };

  const shouldShowCancelButton = () => {
    return !isApp && isProPlan();
  };

  return (
    <>
      {loading ? (
        <></>
      ) : (
        <div className="flex justify-start items-start flex-col h-full p-4 w-full">
          <div className="flex items-center w-full justify-between flex-row">
            <div className="static flex items-center justify-start mb-4">
              {isSmallScreen && (
                <>
                  <IconButton
                    color="inherit"
                    edge="start"
                    className="mr-2"
                    onClick={() => {
                      history.replace("/setting");
                    }}
                  >
                    <ArrowBackIcon />
                  </IconButton>
                  <div className="flex">
                    <Typography variant="h6" style={{ textAlign: "center" }}>
                      Usage
                    </Typography>
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-4 w-full">
            {/* Subscription Information Card */}
            <Card
              className="w-full"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                boxShadow: "none",
              }}
            >
              <CardContent style={{ paddingTop: "16px" }}>
                <div className="flex flex-col gap-4">
                  <Typography
                    variant="h6"
                    component="div"
                    style={{
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}
                  >
                    Subscription
                  </Typography>
                  <div className="flex flex-col gap-2">
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        <AutorenewIcon
                          style={{ marginRight: "8px", fontSize: "1.2rem" }}
                          color="primary"
                        />
                        <Typography style={{ fontSize: "0.95rem" }}>
                          Plan
                        </Typography>
                      </div>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {getPlanName(credits?.currentTier)}
                      </Typography>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="flex items-center">
                        {credits?.currentTier === credits?.refreshTier ? (
                          <EventAvailableIcon
                            style={{ marginRight: "8px", fontSize: "1.2rem" }}
                            color="primary"
                          />
                        ) : (
                          <EventBusyIcon
                            style={{ marginRight: "8px", fontSize: "1.2rem" }}
                            color="primary"
                          />
                        )}
                        <Typography style={{ fontSize: "0.95rem" }}>
                          {getRefreshLabel()}
                        </Typography>
                      </div>
                      <Typography variant="body1" style={{ fontWeight: 500 }}>
                        {dayjs(credits?.nextRefreshTimestamp).format(
                          "MMMM D, YYYY"
                        )}
                      </Typography>
                    </div>
                  </div>

                  {/* Upgrade/Cancel Button - Show based on plan */}
                  {shouldShowUpgradeButton() && (
                    <div
                      className="flex justify-center"
                      style={{ marginTop: "24px" }}
                    >
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={handleUpgrade}
                        className="transition-all"
                        style={{
                          borderRadius: "12px",
                          textTransform: "none",
                          paddingLeft: "40px",
                          paddingRight: "40px",
                          paddingTop: "12px",
                          paddingBottom: "12px",
                          fontSize: "1rem",
                          fontWeight: 600,
                          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                          background:
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 6px 16px rgba(0, 0, 0, 0.2)";
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #5a67d8 0%, #6a42a0 100%)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.boxShadow =
                            "0 4px 12px rgba(0, 0, 0, 0.15)";
                          e.currentTarget.style.background =
                            "linear-gradient(135deg, #667eea 0%, #764ba2 100%)";
                        }}
                      >
                        Upgrade to Pro
                      </Button>
                    </div>
                  )}
                  {shouldShowCancelButton() && (
                    <div
                      className="flex justify-center"
                      style={{ marginTop: "24px" }}
                    >
                      <Button
                        variant="outlined"
                        onClick={() => setCancelDialogOpen(true)}
                        className="transition-colors"
                        style={{
                          borderRadius: "12px",
                          textTransform: "none",
                          paddingLeft: "40px",
                          paddingRight: "40px",
                          paddingTop: "12px",
                          paddingBottom: "12px",
                          fontSize: "0.95rem",
                          fontWeight: 500,
                          borderColor: "rgba(255, 59, 48, 0.5)",
                          color: "#FF3B30",
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = "#FF3B30";
                          e.currentTarget.style.backgroundColor =
                            "rgba(255, 59, 48, 0.08)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor =
                            "rgba(255, 59, 48, 0.5)";
                          e.currentTarget.style.backgroundColor = "transparent";
                        }}
                      >
                        Cancel Subscription
                      </Button>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Usage Information Card */}
            <Card
              className="w-full"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                boxShadow: "none",
              }}
            >
              <CardContent style={{ paddingTop: "16px" }}>
                <div className="flex flex-col gap-4">
                  <Typography
                    variant="h6"
                    component="div"
                    style={{
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}
                  >
                    Usage
                  </Typography>
                  <div className="flex flex-col gap-2">
                    <div className="flex items-center gap-2">
                      <TokenIcon
                        style={{ fontSize: "1.2rem" }}
                        color="primary"
                      />
                      <Typography
                        style={{
                          fontSize: "0.95rem",
                          color: "hsl(var(--muted-foreground))",
                        }}
                      >
                        Token
                      </Typography>
                    </div>
                    <div
                      className="flex justify-between items-center"
                      style={{ marginBottom: "8px" }}
                    >
                      <Typography variant="h4" style={{ fontWeight: 600 }}>
                        {formatBalance(credits?.usedBalance)}
                      </Typography>
                      <Typography
                        variant="h4"
                        style={{
                          fontWeight: 600,
                          color: "hsl(var(--muted-foreground))",
                        }}
                      >
                        / {formatBalance(credits?.totalBalance)}
                      </Typography>
                    </div>
                    <LinearProgress
                      variant="determinate"
                      value={getUsagePercentage()}
                      style={{
                        height: 8,
                        borderRadius: 4,
                        backgroundColor: "rgba(0,0,0,0.1)",
                      }}
                      sx={{
                        "& .MuiLinearProgress-bar": {
                          borderRadius: 4,
                        },
                      }}
                    />

                    {/* Usage Breakdown Section */}
                    <div
                      className="flex flex-col gap-3"
                      style={{
                        marginTop: "24px",
                        padding: "16px",
                        borderRadius: "8px",
                        backgroundColor: "rgba(0,0,0,0.03)",
                      }}
                    >
                      <Typography
                        style={{
                          fontSize: "0.85rem",
                          fontWeight: 600,
                          color: "hsl(var(--muted-foreground))",
                          textTransform: "uppercase",
                          letterSpacing: "0.5px",
                        }}
                      >
                        Usage Breakdown
                      </Typography>

                      {/* Automation Usage */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <AutoModeIcon
                            style={{ fontSize: "1.1rem" }}
                            color="primary"
                          />
                          <Typography style={{ fontSize: "0.9rem" }}>
                            Automations
                          </Typography>
                          <Tooltip
                            title="Tokens used by active automations running in the Cloud environment. One active (executed at least once) automation in the billing cycle always consumes 1 token, no matter how many times it is executed. Locally running automations do not consume tokens."
                            arrow
                            placement="top"
                            open={openTooltip === "automation"}
                            onClose={() => setOpenTooltip(null)}
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                setOpenTooltip(
                                  openTooltip === "automation"
                                    ? null
                                    : "automation"
                                )
                              }
                              style={{
                                padding: 0,
                                marginLeft: "4px",
                              }}
                            >
                              <InfoOutlinedIcon
                                style={{
                                  fontSize: "0.95rem",
                                  color: "hsl(var(--muted-foreground))",
                                  opacity: 0.7,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 600, fontSize: "0.95rem" }}
                        >
                          {formatBalance(credits?.usedAutomation)}
                        </Typography>
                      </div>

                      {/* Chat Usage */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <TokenIcon
                            style={{ fontSize: "1.1rem" }}
                            color="primary"
                          />
                          <Typography style={{ fontSize: "0.9rem" }}>
                            Chat
                          </Typography>
                          <Tooltip
                            title="Tokens used in assistant chat conversations."
                            arrow
                            placement="top"
                            open={openTooltip === "chat"}
                            onClose={() => setOpenTooltip(null)}
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                setOpenTooltip(
                                  openTooltip === "chat" ? null : "chat"
                                )
                              }
                              style={{
                                padding: 0,
                                marginLeft: "4px",
                              }}
                            >
                              <InfoOutlinedIcon
                                style={{
                                  fontSize: "0.95rem",
                                  color: "hsl(var(--muted-foreground))",
                                  opacity: 0.7,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 600, fontSize: "0.95rem" }}
                        >
                          {formatBalance(credits?.usedChat)}
                        </Typography>
                      </div>

                      {/* Integration Usage */}
                      <div className="flex justify-between items-center">
                        <div className="flex items-center gap-2">
                          <EventAvailableIcon
                            style={{ fontSize: "1.1rem" }}
                            color="primary"
                          />
                          <Typography style={{ fontSize: "0.9rem" }}>
                            Integrations
                          </Typography>
                          <Tooltip
                            title="Tokens used by certain integration invocations, like LLM related integrations. Most integrations do not consume tokens. Check each integration for more details."
                            arrow
                            placement="top"
                            open={openTooltip === "integration"}
                            onClose={() => setOpenTooltip(null)}
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                          >
                            <IconButton
                              size="small"
                              onClick={() =>
                                setOpenTooltip(
                                  openTooltip === "integration"
                                    ? null
                                    : "integration"
                                )
                              }
                              style={{
                                padding: 0,
                                marginLeft: "4px",
                              }}
                            >
                              <InfoOutlinedIcon
                                style={{
                                  fontSize: "0.95rem",
                                  color: "hsl(var(--muted-foreground))",
                                  opacity: 0.7,
                                }}
                              />
                            </IconButton>
                          </Tooltip>
                        </div>
                        <Typography
                          variant="body1"
                          style={{ fontWeight: 600, fontSize: "0.95rem" }}
                        >
                          {formatBalance(credits?.usedIntegration)}
                        </Typography>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Billing History Card */}
            <Card
              className="w-full"
              style={{
                backgroundColor: getComponentBgColor("hsl(var(--card))"),
                boxShadow: "none",
              }}
            >
              <CardContent style={{ paddingTop: "16px" }}>
                <div className="flex flex-col gap-4">
                  <Typography
                    variant="h6"
                    component="div"
                    style={{
                      fontWeight: 500,
                      marginBottom: "8px",
                    }}
                  >
                    Billing History
                  </Typography>

                  {payments.length === 0 ? (
                    <Typography color="text.secondary">
                      No payment history available
                    </Typography>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {payments.map((payment, index) => (
                        <div
                          key={index}
                          className="flex justify-between items-center"
                          style={{
                            paddingTop: "8px",
                            paddingBottom: "8px",
                            borderBottom:
                              index < payments.length - 1
                                ? `1px solid hsl(var(--border))`
                                : "none",
                          }}
                        >
                          <div>
                            <Typography
                              variant="body1"
                              style={{ fontWeight: 500 }}
                            >
                              {payment.description}
                            </Typography>
                            <Typography
                              variant="body2"
                              style={{
                                color: "hsl(var(--muted-foreground))",
                                fontSize: "0.875rem",
                              }}
                            >
                              {customFormat(
                                dayjs(payment.timestamp),
                                currentTime
                              )}
                            </Typography>
                          </div>
                          <Typography
                            variant="h6"
                            style={{
                              color: "hsl(var(--primary))",
                              fontWeight: "bold",
                            }}
                          >
                            ${(payment.amount / 100).toFixed(2)}
                          </Typography>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Cancel Subscription Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        PaperProps={{
          style: {
            borderRadius: "12px",
            minWidth: "320px",
          },
        }}
      >
        <DialogTitle style={{ fontWeight: 600 }}>
          Cancel Subscription?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            {
              "Are you sure you want to cancel your Pro subscription? You'll lose access to Pro features at the end of your current billing period."
            }
          </Typography>
        </DialogContent>
        <DialogActions
          style={{
            paddingLeft: "24px",
            paddingRight: "24px",
            paddingBottom: "16px",
          }}
        >
          <Button
            onClick={() => setCancelDialogOpen(false)}
            variant="outlined"
            style={{
              textTransform: "none",
              color: "hsl(var(--muted-foreground))",
            }}
          >
            Keep Subscription
          </Button>
          <Button
            onClick={handleCancelSubscription}
            variant="contained"
            className="transition-colors"
            style={{
              textTransform: "none",
              backgroundColor: "#FF3B30",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#e6342a";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#FF3B30";
            }}
          >
            Yes, Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default Usage;
